/**
 * Utility functions for Agenda Print Preview and Word Document (.doc) Export.
 */

/**
 * Renders attendees in a compact, multi-column format displaying only Name and Present status.
 * Replaces the space-consuming 4-column table with a condensed 2-column checklist grid.
 */
function renderCondensedAttendeesHtml(list) {
  if (!list || list.length === 0) {
    return `<div style="font-style: italic; color: #718096; font-size: 12px; padding: 4px 0; margin-bottom: 12px;">No attendees listed</div>`;
  }

  const validList = list.filter(item => item && (item.name || '').trim().length > 0);
  if (validList.length === 0) {
    return `<div style="font-style: italic; color: #718096; font-size: 12px; padding: 4px 0; margin-bottom: 12px;">No attendees listed</div>`;
  }

  const columns = 2;
  let html = `
    <table style="width: 100%; border-collapse: collapse; margin-bottom: 14px; background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 4px;">
  `;

  for (let i = 0; i < validList.length; i += columns) {
    html += `<tr>`;
    for (let c = 0; c < columns; c++) {
      const attendee = validList[i + c];
      if (attendee) {
        const isPresent = Boolean(attendee.present);
        const checkmark = isPresent ? '&#9745;' : '&#9744;'; // ☑ vs ☐
        const checkColor = isPresent ? '#17365D' : '#a0aec0';
        const nameColor = isPresent ? '#1a202c' : '#718096';
        
        html += `
          <td style="width: ${100 / columns}%; padding: 4px 10px; font-size: 12px; vertical-align: middle; border: none;">
            <span style="font-size: 14px; font-weight: bold; color: ${checkColor}; margin-right: 6px;">${checkmark}</span>
            <span style="color: ${nameColor}; font-weight: ${isPresent ? '600' : 'normal'};">${attendee.name}</span>
          </td>
        `;
      } else {
        html += `<td style="width: ${100 / columns}%; border: none;"></td>`;
      }
    }
    html += `</tr>`;
  }

  html += `</table>`;
  return html;
}

/**
 * Helper to render tabular data rows for Opportunities, Progress, and Action Items.
 */
function renderTableRows(list, columns, emptyText) {
  if (!list || list.length === 0) {
    return `<tr><td colspan="${columns.length}" align="center" style="font-style: italic; color: #718096; font-size: 12px; padding: 5px;">${emptyText}</td></tr>`;
  }
  return list.map(row => {
    return `<tr>` + columns.map(col => {
      let val = row[col];
      if (col === 'present') {
        val = val ? 'Yes' : 'No';
      }
      return `
        <td style="font-size: 12px; color: #2d3748; padding: 4px 6px; border: 1px solid #cbd5e0;">
          ${val || ''}
        </td>
      `;
    }).join('') + `</tr>`;
  }).join('');
}

/**
 * Builds the complete HTML document string for an Agenda.
 */
export function generateAgendaHtml(agenda, companyLogo, isWord = false) {
  const locationLink = agenda.locationUrl 
    ? `<a href="${agenda.locationUrl}" target="_blank" rel="noopener noreferrer">${agenda.locationName || 'Meeting Link'}</a>`
    : (agenda.locationName || 'N/A');

  const wordHeader = isWord ? `
    <!--[if gte mso 9]>
    <xml>
      <w:WordDocument>
        <w:View>Print</w:View>
        <w:Zoom>100</w:Zoom>
      </w:WordDocument>
    </xml>
    <![endif]-->
  ` : '';

  return `
    <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
    <head>
      <title>${agenda.title || 'Agenda'}</title>
      <meta charset="utf-8">
      ${wordHeader}
      <style>
        body { 
          font-family: 'Segoe UI', Arial, sans-serif; 
          line-height: 1.35; 
          color: #333333; 
          padding: 20px; 
          margin: 0;
          background: #ffffff;
        }
        h1 { 
          color: #17365D; 
          font-size: 18px; 
          border-bottom: 2px solid #17365D; 
          padding: 0 0 4px 0; 
          margin: 0 0 10px 0; 
          text-transform: uppercase; 
          letter-spacing: 0.5px; 
        }
        h2 { 
          color: #17365D; 
          font-size: 13px; 
          margin: 12px 0 4px 0; 
          border-bottom: 1px solid #e2e8f0; 
          padding: 0 0 2px 0; 
          text-transform: uppercase; 
          font-weight: bold; 
        }
        p { margin: 0 0 4px 0; font-size: 12px; }
        .meta-info { 
          background-color: #f7fafc; 
          border: 1px solid #e2e8f0; 
          padding: 6px 10px; 
          border-radius: 4px; 
          margin-bottom: 12px; 
        }
        .meta-info table { width: 100%; border-collapse: collapse; }
        .meta-info td { padding: 2px 6px; font-size: 12px; vertical-align: top; }
        .meta-label { font-weight: bold; color: #4a5568; width: 120px; }
        table.data-table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
        table.data-table th { 
          background-color: #17365D; 
          color: white; 
          font-size: 12px; 
          font-weight: bold; 
          padding: 4px 6px; 
          border: 1px solid #cbd5e0; 
          text-align: left; 
        }
        @media print {
          body { padding: 0; }
          .no-print { display: none; }
          @page { margin: 0.5in; size: portrait; }
        }
      </style>
    </head>
    <body>
      <div style="display: flex; justify-content: flex-start; text-align: left; margin-bottom: 12px;">
        <img src="${companyLogo || 'https://jimini-logistics.web.app/favicon.png'}" style="max-height: 50px; max-width: 50px; width: 50px; height: 50px; object-fit: contain;" alt="Company Logo" />
      </div>
      
      <h1>${agenda.title || 'MEETING AGENDA'}</h1>
      
      <div class="meta-info">
        <table>
          <tr>
            <td class="meta-label">DATE:</td>
            <td>${agenda.date || 'N/A'}</td>
          </tr>
          <tr>
            <td class="meta-label">TIME:</td>
            <td>${agenda.time || 'N/A'}</td>
          </tr>
          <tr>
            <td class="meta-label">LOCATION:</td>
            <td>${locationLink}</td>
          </tr>
          <tr>
            <td class="meta-label">ORGANIZER:</td>
            <td>${agenda.organizer || 'N/A'}</td>
          </tr>
        </table>
      </div>

      <h2>ATTENDEES</h2>
      ${renderCondensedAttendeesHtml(agenda.attendeesList)}

      <h2>CURRENT OPPORTUNITIES</h2>
      <table class="data-table">
        <thead>
          <tr>
            <th style="padding: 4px 6px; width: 25%;">Opportunity</th>
            <th style="padding: 4px 6px; width: 15%;">Stage</th>
            <th style="padding: 4px 6px; width: 15%;">Value</th>
            <th style="padding: 4px 6px; width: 25%;">Next Milestone</th>
            <th style="padding: 4px 6px; width: 20%;">Owner(s)</th>
          </tr>
        </thead>
        <tbody>
          ${renderTableRows(agenda.currentOppsList, ['opportunity', 'stage', 'value', 'nextMilestone', 'owners'], 'No current opportunities listed')}
        </tbody>
      </table>

      <h2>NEW OPPORTUNITIES</h2>
      <table class="data-table">
        <thead>
          <tr>
            <th style="padding: 4px 6px; width: 25%;">Opportunity</th>
            <th style="padding: 4px 6px; width: 20%;">Customer/Agency</th>
            <th style="padding: 4px 6px; width: 15%;">Est. Value</th>
            <th style="padding: 4px 6px; width: 20%;">Qualifications</th>
            <th style="padding: 4px 6px; width: 20%;">Owner(s)</th>
          </tr>
        </thead>
        <tbody>
          ${renderTableRows(agenda.newOppsList, ['opportunity', 'customerAgency', 'estValue', 'qualifications', 'owners'], 'No new opportunities listed')}
        </tbody>
      </table>

      <h2>BUSINESS DEVELOPMENT PROGRESS</h2>
      <table class="data-table">
        <thead>
          <tr>
            <th style="padding: 4px 6px; width: 25%;">Initiative</th>
            <th style="padding: 4px 6px; width: 25%;">Progress</th>
            <th style="padding: 4px 6px; width: 30%;">Notes</th>
            <th style="padding: 4px 6px; width: 20%;">Owner(s)</th>
          </tr>
        </thead>
        <tbody>
          ${renderTableRows(agenda.progressList, ['initiative', 'progress', 'notes', 'owners'], 'No progress details listed')}
        </tbody>
      </table>

      <h2>CALL TO ACTION ITEMS</h2>
      <table class="data-table">
        <thead>
          <tr>
            <th style="padding: 4px 6px; width: 40%;">Action Item</th>
            <th style="padding: 4px 6px; width: 20%;">Owner</th>
            <th style="padding: 4px 6px; width: 20%;">Due Date</th>
            <th style="padding: 4px 6px; width: 20%;">Status</th>
          </tr>
        </thead>
        <tbody>
          ${renderTableRows(agenda.actionItemsList, ['task', 'assignedTo', 'dueDate', 'status'], 'No action items listed')}
        </tbody>
      </table>

      <h2>KEEP AWARE</h2>
      <table class="data-table">
        <thead>
          <tr>
            <th style="padding: 4px 6px; width: 35%;">Item / Topic</th>
            <th style="padding: 4px 6px; width: 45%;">Notes & Details</th>
            <th style="padding: 4px 6px; width: 20%;">Owner / POC</th>
          </tr>
        </thead>
        <tbody>
          ${renderTableRows(agenda.keepAwareList, ['item', 'notes', 'owner'], 'No keep aware items listed')}
        </tbody>
      </table>
    </body>
    </html>
  `;
}

/**
 * Exports the given agenda data into a Microsoft Word (.doc) file.
 */
export function exportAgendaToWord(agenda, companyLogo) {
  const html = generateAgendaHtml(agenda, companyLogo, true);
  const blob = new Blob(['\ufeff' + html], {
    type: 'application/msword'
  });

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${(agenda.title || 'Agenda').replace(/\s+/g, '_')}_Agenda.doc`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Opens a browser print dialog for the given agenda data.
 */
export function printAgenda(agenda, companyLogo) {
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    alert('Please allow pop-ups to print the meeting agenda.');
    return;
  }

  const html = generateAgendaHtml(agenda, companyLogo, false);
  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.write(`
    <script>
      window.onload = function() {
        window.print();
        setTimeout(function() {
          window.close();
        }, 500);
      };
    </script>
  `);
  printWindow.document.close();
}
